import React, { useState } from 'react'
import { Subscription } from '../../types'
import { format } from 'date-fns'
import { MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline'
import Spinner from '../Spinner'
import { mediaUrl } from '../../utils/mediaUrl'

/** The subscriber's name (the API's `subscriber`; `creator` is you). */
const subscriberName = (sub: Subscription): string =>
  sub.subscriber?.full_name || sub.subscriber?.username || ''

interface SubscribersListProps {
  subscribers: Subscription[]
  isLoading?: boolean
  /** The creator's public page, offered to share when there are no subscribers. */
  pageUrl?: string | null
}

const SubscribersList: React.FC<SubscribersListProps> = ({
  subscribers,
  isLoading = false,
  pageUrl = null,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [filterTier, setFilterTier] = useState<number | 'all'>('all')
  const [filterStatus, setFilterStatus] = useState<string | 'all'>('all')

  const uniqueTiers = Array.from(
    new Set(subscribers.map((s) => s.tier?.id).filter(Boolean))
  )

  const filteredSubscribers = subscribers.filter((sub) => {
    const matchesSearch =
      searchTerm === '' ||
      subscriberName(sub).toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.tier?.name?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesTier = filterTier === 'all' || sub.tier_id === filterTier
    const matchesStatus = filterStatus === 'all' || sub.status === filterStatus

    return matchesSearch && matchesTier && matchesStatus
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'cancelled':
        return 'bg-gray-100 text-gray-800'
      case 'expired':
        return 'bg-red-100 text-red-800'
      case 'past_due':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Subscribers</h2>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search subscribers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <select
            value={filterTier}
            onChange={(e) =>
              setFilterTier(e.target.value === 'all' ? 'all' : parseInt(e.target.value))
            }
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Tiers</option>
            {uniqueTiers.map((tierId) => {
              const tier = subscribers.find((s) => s.tier_id === tierId)?.tier
              return (
                <option key={tierId} value={tierId}>
                  {tier?.name || 'Unknown Tier'}
                </option>
              )
            })}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="cancelled">Cancelled</option>
            <option value="expired">Expired</option>
            <option value="past_due">Past Due</option>
          </select>
        </div>
      </div>

      {filteredSubscribers.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">
            {subscribers.length === 0
              ? 'No subscribers yet — share your page'
              : 'No matching subscribers found.'}
          </p>
          {subscribers.length === 0 && pageUrl && (
            <a
              href={pageUrl}
              className="inline-block mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium break-all px-4"
            >
              {pageUrl}
            </a>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subscriber
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Started
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Next Billing
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSubscribers.map((subscriber) => (
                <tr key={subscriber.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        {subscriber.subscriber?.avatar_url ? (
                          <img
                            className="h-10 w-10 rounded-full"
                            src={mediaUrl(subscriber.subscriber.avatar_url)}
                            alt=""
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <UserIcon className="h-6 w-6 text-gray-500" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {subscriberName(subscriber) || 'Unknown User'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {subscriber.subscriber
                            ? `@${subscriber.subscriber.username}`
                            : `ID: ${subscriber.subscriber_id}`}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{subscriber.tier?.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                        subscriber.status
                      )}`}
                    >
                      {subscriber.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    $
                    {(
                      (subscriber.is_annual && subscriber.tier?.price_yearly_cents != null
                        ? subscriber.tier.price_yearly_cents
                        : subscriber.tier?.price_monthly_cents ?? 0) / 100
                    ).toFixed(2)}
                    /{subscriber.is_annual ? 'yr' : 'mo'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {format(new Date(subscriber.created_at), 'MMM d, yyyy')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {subscriber.cancel_at_period_end
                      ? 'Cancelled'
                      : format(new Date(subscriber.current_period_end), 'MMM d, yyyy')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 text-sm text-gray-600">
        Showing {filteredSubscribers.length} of {subscribers.length} subscribers
      </div>
    </div>
  )
}

export default SubscribersList
